import { ChevronDown, X } from "lucide-react";
import {
  type ChangeEvent,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { CATEGORIES } from "../lib/categories";
import { UNIT_CATEGORIES, UNIT_CATEGORY_NAMES } from "../lib/units";
import {
  focusFirstInvalidField,
  validateShoppingListItemForm,
} from "../lib/validation";
import { getUserHouseholdId } from "../services/householdService";
import type { ShoppingListItem } from "../services/shoppingListService";

interface ShoppingListItemFormProps {
  onClose: () => void;
  onItemAdded: (
    item: Omit<
      ShoppingListItem,
      "id" | "created_at" | "user_id" | "purchased" | "updated_at"
    >,
  ) => Promise<void> | void;
}

type FormErrors = {
  itemName?: string;
  quantity?: string;
};

// ShoppingListItemForm component
// - Modal form to add an item to the shopping list
// - Validates input using shared helpers and focuses the first invalid field
// - Prevents body scrolling and implements a focus trap while open
// - Calls`onItemAdded` with the new item data and closes on success

export default function ShoppingListItemForm({
  onClose,
  onItemAdded,
}: ShoppingListItemFormProps) {
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState("each (ea)");
  const [category, setCategory] = useState<string>("produce");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const modalRef = useRef<HTMLDivElement>(null);

  // Refs for focus-on-error behavior
  const firstFocusableRef = useRef<HTMLInputElement>(null);
  const itemNameRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);

  useFocusTrap(modalRef, onClose);

  useEffect(() => {
    firstFocusableRef.current?.focus();
  }, []);

  const handleItemNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setItemName(e.target.value);
    if (formErrors.itemName)
      setFormErrors((prev) => ({ ...prev, itemName: undefined }));
    if (error) setError("");
  };

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuantity(e.target.value);
    if (formErrors.quantity)
      setFormErrors((prev) => ({ ...prev, quantity: undefined }));
    if (error) setError("");
  };

  const handleUnitChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setUnit(e.target.value);
  };

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    // Validate using centralized helper
    const errors = validateShoppingListItemForm({ itemName, quantity });
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Focus the first invalid field using shared helper
      focusFirstInvalidField(
        errors,
        { itemName: itemNameRef, quantity: quantityRef },
        ["itemName", "quantity"],
      );
      return;
    }

    // Clear previous errors
    setFormErrors({});
    setLoading(true);

    try {
      const householdId = await getUserHouseholdId();
      if (!householdId) {
        throw new Error(
          "Could not find your household. Please ensure you are part of one.",
        );
      }
      await onItemAdded({
        household_id: householdId,
        item_name: itemName.trim(),
        quantity: Number(quantity),
        unit,
        category,
      });
      toast.success(`"${itemName.trim()}" added to shopping list`);
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add item.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="pt-safe fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2
            id="modal-title"
            className="text-xl font-bold text-gray-800 dark:text-gray-100"
          >
            Add to Shopping List
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close dialog"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="itemName"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Item Name{" "}
              <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              ref={(el) => {
                firstFocusableRef.current = el ?? null;
                itemNameRef.current = el ?? null;
              }}
              id="itemName"
              type="text"
              value={itemName}
              onChange={handleItemNameChange}
              placeholder="e.g. Milk, Chicken, Carrots"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 placeholder-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500"
              aria-describedby={
                formErrors.itemName
                  ? "itemName-error"
                  : error
                    ? "form-error"
                    : undefined
              }
              aria-invalid={!!formErrors.itemName || !!error}
              required
            />
            {formErrors.itemName && (
              <p
                id="itemName-error"
                className="mt-1 text-sm text-red-700 dark:text-red-400"
              >
                {formErrors.itemName}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="quantity"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Quantity{" "}
                <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                ref={quantityRef}
                id="quantity"
                type="number"
                step="0.25"
                min="0"
                value={quantity}
                onChange={handleQuantityChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                aria-describedby={
                  formErrors.quantity ? "quantity-error" : undefined
                }
                aria-invalid={!!formErrors.quantity}
                required
              />
              {formErrors.quantity && (
                <p
                  id="quantity-error"
                  className="mt-1 text-sm text-red-700 dark:text-red-400"
                >
                  {formErrors.quantity}
                </p>
              )}
            </div>
            <div className="relative">
              <label
                htmlFor="unit"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Unit
              </label>
              <select
                id="unit"
                value={unit}
                onChange={handleUnitChange}
                className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              >
                {UNIT_CATEGORY_NAMES.map((cat) => (
                  <optgroup key={cat} label={cat}>
                    {UNIT_CATEGORIES[cat].map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute top-9 right-3 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <fieldset>
            <legend className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Category <span className="text-red-600 dark:text-red-400">*</span>
            </legend>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {CATEGORIES.map((cat) => (
                <button
                  type="button"
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`rounded-lg px-3 py-2 text-center text-xs font-medium capitalize ${
                    category === cat
                      ? "bg-blue-600 text-white dark:bg-blue-500"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {cat.replace("_", " ")}
                </button>
              ))}
            </div>
          </fieldset>

          {error && (
            <div
              id="form-error"
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400"
            >
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-opacity hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {loading ? "Adding..." : "Add Item"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
